"use client";
import { Link, usePathname, useRouter } from "@/i18n/routing";
import { Button, cn, Spacer } from "@heroui/react";
import _ from "lodash";
import { useTranslations } from "next-intl";
import React, { useMemo } from "react";
import Image from "next/image";
import { CustomScroll } from "@pikabobalex/react-custom-scroll";
import useLogoutAction from "@/components/hooks/use-logout-action";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { categoryAtom, searchAtom } from "@/components/_store";
import UserProfileCard from "./user-profile-card";
import useSWRFetcher from "./hooks/use-swr-fetcher";
import { GameListQuery } from "@/app/api/game-list/type";
import { GameType } from "@/lib/declarations";
// import UserProfileCard from "./user-profile-card";

type MenuContentProps = {
  onClose?: () => void;
  isLoggedIn: boolean;
  heightRelativeToParent?: string;
};
const MenuContent = ({ onClose, isLoggedIn, heightRelativeToParent }: MenuContentProps) => {
  const t = useTranslations();
  const router = useRouter();
  const pathname = usePathname();
  const [category, setCategory] = useAtom(categoryAtom);
  const performLogout = useLogoutAction(() => onClose?.());
  const categoryItems = ["recent", "hot-games", "slot", "cricket", "live", "table", "sports", "fishing", "crash"];
  const search = useAtomValue(searchAtom);
  const { data: category_data, error: category_error } = useSWRFetcher<GameListQuery>(
    // `/api/game-list?length=0&search=${search}&category=${GameType[category?.toUpperCase() as keyof typeof GameType] || 6}`,
    isLoggedIn
      ? [
          "/api/game-list?",
          "length=0",
          `search=${search || ""}`,
          `category=${GameType[category?.toUpperCase() as keyof typeof GameType] || 6}`,
        ].join("&")
      : "",
    {
      keepPreviousData: true,
      revalidateOnFocus: true,
    }
  );
  const menuItems = useMemo(() => {
    return {
      deposit: "/deposit",
      withdraw: "/withdrawal",
      rewards: "/rewards",
      "vip-privileges": "/vip",
      "promo-code": "/profile/promo-code",
      "lp-store": "/lp-store",
      "bank-details": "/profile/bank-details",
      "change-password": "/profile/change-password",
      "bet-history": "/wallet/wallet-history/bet-history",
      "edit-profile": "/profile/personal-info",
      // faqs: "/#",
      // tnc: "/#",
      // "privacy-policy": "/#",
      ...(isLoggedIn && { logout: "/#" }),
    };
  }, [isLoggedIn]);
  return (
    <CustomScroll heightRelativeToParent={heightRelativeToParent}>
      <Spacer y={4} />
      {isLoggedIn && <UserProfileCard className="h-auto px-4" />}
      <div className="p-4 pb-12">
        <div className="grid grid-cols-3 gap-3">
          {categoryItems.map((e, i) => {
            const tag = (e: string) => {
              switch (e) {
                case "recent":
                  return "recents";
                case "hot-games":
                  return "hot_games";
                case "table":
                  return "table_games";
                default:
                  return e;
              }
            };

            return (
              <Button
                // as={Link}
                // href="/"
                isDisabled={
                  isLoggedIn && e !== "recent" && !category_data?.categories.find((category) => category === tag(e))
                }
                onPress={() => {
                  onClose?.();
                  if (!isLoggedIn) {
                    router.push("/login");
                    return;
                  }
                  setCategory(tag(e));
                  !/^\/$/.test(pathname) && router.push("/");
                }}
                key={i}
                className={cn(
                  "flex aspect-[0.9] size-full flex-col items-center rounded-md bg-slate-400 p-2 text-center text-sm leading-none",
                  `gradient-primary`
                )}
              >
                <span className="text-nowrap font-light text-white text-tiny">{t("menu-" + e)}</span>
                <Image src={`/category/${e}.webp`} width={48} height={48} alt="" />
              </Button>
            );
          })}
        </div>
        <Spacer y={3} />
        <ol className="space-y-3">
          {_.keys(menuItems).map((e, i) => {
            const href = menuItems[e as keyof typeof menuItems];

            const getIcon = (e: string) => {
              let out: string = "";
              switch (e) {
                case "rewards":
                  out = "icon-icon33";
                  break;
                case "deposit":
                  out = "icon-icon56";
                  break;
                case "vip-privileges":
                  out = "icon-icon32";
                  break;
                case "lp-store":
                  out = "icon-icon40";
                  break;
                case "bank-details":
                  out = "icon-icon34";
                  break;
                case "change-password":
                  out = "icon-icon43";
                  break;
                case "edit-profile":
                  out = "icon-icon41";
                  break;
                case "bet-history":
                  out = "icon-icon14";
                  break;
                case "logout":
                  out = "icon-icon29";
                  break;
                case "withdraw":
                  out = "icon-icon57";
                  break;
                case "promo-code":
                  out = "icon-icon81";
                  break;
                default:
                  out = "";
                  break;
              }
              return out;
            };

            const InnerContent = () => (
              <>
                <span className={cn("me-2 inline-block text-[1.5em]", getIcon(e))} />
                <span>{t("menu-" + e)}</span>
                <span className="icon-icon7 ms-auto inline-block text-[1.25em]" />
              </>
            );
            return (
              <li key={i} className="inline-flex w-full">
                {(e == "logout" && (
                  <Button
                    onPress={performLogout}
                    variant="light"
                    fullWidth
                    // color="secondary"
                    className={cn("inline-flex h-12 items-center justify-center")}
                  >
                    <span className={cn("me-2 inline-block text-[1.5em]", getIcon(e))} />
                    <span>{t("menu-" + e)}</span>
                    {/* <InnerContent /> */}
                  </Button>
                )) || (
                  <Button
                    as={Link}
                    href={href}
                    onPress={onClose}
                    color="secondary"
                    fullWidth
                    className={cn("inline-flex h-8 items-center justify-center", {
                      "gradient-secondary font-bold text-white": pathname === href,
                    })}
                  >
                    <InnerContent />
                  </Button>
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </CustomScroll>
  );
};

export default MenuContent;

// t("menu-recent")
// t("menu-hot-games")
// t("menu-slot")
// t("menu-cricket")
// t("menu-live")
// t("menu-table")
// t("menu-sports")
// t("menu-fishing")
// t("menu-crash")

// t("menu-deposit")
// t("menu-withdraw")
// t("menu-rewards")
// t("menu-vip-privileges")
// t("menu-lp-store")
// t("menu-bank-details")
// t("menu-change-password")
// t("menu-bet-history")
// t("menu-edit-profile")
// t("menu-faqs")
// t("menu-tnc")
// t("menu-privacy-policy")

// t("menu-logout")
